import { createContext, useContext, useEffect, useState } from 'react';
import env from '@architecture-it/react-env';

const GoogleMapsContext = createContext({
  isLoaded: false,
  loadError: null,
});

export const GoogleMapsProvider = ({ children }) => {
  const [state, setState] = useState({
    isLoaded: false,
    loadError: null,
  });

  useEffect(() => {
    let cancelled = false;

    const isFullyLoaded = () => window.google && window.google.maps && window.google.maps.places;

    if (isFullyLoaded()) {
      setState({ isLoaded: true, loadError: null });
      return undefined;
    }

    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const check = setInterval(() => {
        if (isFullyLoaded()) {
          clearInterval(check);
          if (!cancelled) {
            setState({ isLoaded: true, loadError: null });
          }
        }
      }, 100);
      return () => {
        cancelled = true;
        clearInterval(check);
      };
    }

    const key = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY || env('GOOGLE_MAPS_KEY');
    if (!key) {
      setState({
        isLoaded: false,
        loadError: 'Falta configurar NEXT_PUBLIC_GOOGLE_MAPS_KEY',
      });
      return undefined;
    }

    const cbName = '__hopGoogleMapsJsCallback';
    window[cbName] = () => {
      try {
        delete window[cbName];
      } catch {
        window[cbName] = undefined;
      }
      if (cancelled) return;
      // With async loading, sub-libraries may not be immediately available
      const waitForPlaces = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(waitForPlaces);
          if (!cancelled) setState({ isLoaded: true, loadError: null });
        }
      }, 50);
    };

    const script = document.createElement('script');
    const params = new URLSearchParams({
      key,
      libraries: 'places,marker',
      language: 'es',
      loading: 'async',
      callback: cbName,
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      if (window[cbName]) {
        try {
          delete window[cbName];
        } catch {
          window[cbName] = undefined;
        }
      }
      if (!cancelled) {
        setState({ isLoaded: false, loadError: 'Error al cargar Google Maps' });
      }
    };

    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <GoogleMapsContext.Provider value={state}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMaps = () => useContext(GoogleMapsContext);